import {TrackTable} from '@app/web-player/tracks/track-table/track-table';
import {queueGroupId} from '@app/web-player/queue-group-id';
import {Artist} from '@app/web-player/artists/artist';
import {PaginationResponse} from '@common/http/backend-response/pagination-response';
import {Album} from '@app/web-player/albums/album';
import {
  ArtistAlbumQueryOptions,
  useArtistAlbums,
  useArtistAlbumsSubtype,
} from '@app/web-player/artists/requests/use-artist-albums';
import {AlbumImage} from '@app/web-player/albums/album-image/album-image';
import {AlbumLink} from '@app/web-player/albums/album-link';
import {FormattedDate} from '@ui/i18n/formatted-date';
import {Button} from '@ui/buttons/button';
import {ArrowDropDownIcon} from '@ui/icons/material/ArrowDropDown';
import {Trans} from '@ui/i18n/trans';
import {InfiniteScrollSentinel} from '@common/ui/infinite-scroll/infinite-scroll-sentinel';
import {NoDiscographyMessage} from '@app/web-player/artists/artist-page/discography-panel/no-discography-message';
import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import {AlbumContextDialog} from '@app/web-player/albums/album-context-dialog';
import {useSortableTableData} from '@common/ui/tables/use-sortable-table-data';
import { AlbumSubtype } from '@common/admin/use-album_taxonomies';
import { IconButton } from '@ui/buttons/icon-button';
import { GridViewIcon } from '@ui/icons/material/GridView';
import { ViewAgendaIcon } from '@ui/icons/material/ViewAgenda';
import {Tooltip} from '@ui/tooltip/tooltip';
import { useSettings } from '@ui/settings/use-settings';
import { useCookie } from '@ui/utils/hooks/use-cookie';
import { albumLayoutKey } from '@app/web-player/artists/requests/use-artist';
import { ProgressCircle } from '@ui/progress/progress-circle';
import { useEffect, useRef } from 'react';

interface ArtistAlbumsListProps {
  artist: Artist;
  initialAlbums: PaginationResponse<Album> | null;
  albumQueryOptions?: ArtistAlbumQueryOptions;
  showEmptyMessage?: boolean;
}
export function ArtistAlbumsList({
  artist,
  initialAlbums,
  albumQueryOptions,
  showEmptyMessage = true,
}: ArtistAlbumsListProps) {
  const query = useArtistAlbums(
    initialAlbums,
    'list',
    artist ? artist.id : undefined,
    albumQueryOptions,
  );
  const {isLoading, items} = query;

  if (!isLoading && !items.length) {
    if (!showEmptyMessage) {
      return null;
    }
    return <NoDiscographyMessage />;
  }

  return (
    <section>
      {items.map(album => (
        <div key={album.id} className="mb-40">
          <div className="mb-20 items-center gap-14 md:flex">
            <AlbumImage
              album={album}
              size="w-110 h-110"
              className="flex-shrink-0 rounded object-cover"
            />
            <div className="flex-auto">
              <h4 className="min-w-0 overflow-hidden overflow-ellipsis whitespace-nowrap text-lg font-semibold max-md:mt-12">
                <AlbumLink album={album} />
              </h4>
              {album.release_date && (
                <div className="mb-18 mt-2 text-sm text-muted">
                  <FormattedDate date={album.release_date} />
                </div>
              )}
              <DialogTrigger type="popover" mobileType="tray" offset={10}>
                <Button
                  variant="outline"
                  size="xs"
                  radius="rounded-full"
                  endIcon={<ArrowDropDownIcon />}
                >
                  <Trans message="More" />
                </Button>
                <AlbumContextDialog album={album} />
              </DialogTrigger>
            </div>
          </div>
          <AlbumTrackTable album={album} />
        </div>
      ))}
      <InfiniteScrollSentinel query={query} />
    </section>
  );
}

interface ArtistAlbumsSubtypeListProps {
  artist: Artist;
  subtype: AlbumSubtype;
  selectedAlbumLayout?: string;
  initialAlbums: PaginationResponse<Album> | null;
  onLoaded?: () => void;
  albumQueryOptions?: ArtistAlbumQueryOptions;
}
export function ArtistAlbumsSubtypeList({
  artist,
  subtype,
  selectedAlbumLayout,
  initialAlbums,
  onLoaded,
  albumQueryOptions,
}: ArtistAlbumsSubtypeListProps) {
  const {player} = useSettings();
  const query = useArtistAlbumsSubtype(
    initialAlbums,
    'list',
    subtype.slug,
    artist ? artist.id : undefined,
    albumQueryOptions,
  );
  const {isLoading, items} = query;
  const [viewMode, setViewMode] = useCookie(
    albumLayoutKey,
    selectedAlbumLayout || player?.default_artist_view || 'grid',
  );
  const calledRef = useRef(false);
  
  useEffect(() => {
    if (!isLoading && onLoaded && !calledRef.current) {
      onLoaded();
      calledRef.current = true;
    }
  }, [isLoading, onLoaded]);

  if (isLoading || (!isLoading && !items.length)) {
    return null;
  }

  return (
    <>
      <div className="mt-30 mb-30 flex items-center border-b pb-4 text-muted">
        <h2 className="mr-auto text-base">
          <Trans message={subtype.name} />
        </h2>
        {/* <Tooltip label={<Trans message="List view" />}>
          <IconButton
            className="ml-24 flex-shrink-0"
            color={viewMode === 'list' ? 'primary' : undefined}
            onClick={() => setViewMode('list')}
          >
            <ViewAgendaIcon />
          </IconButton>
        </Tooltip>
        <Tooltip label={<Trans message="Grid view" />}>
          <IconButton
            className="flex-shrink-0"
            color={viewMode === 'grid' ? 'primary' : undefined}
            onClick={() => setViewMode('grid')}
          >
            <GridViewIcon />
          </IconButton>
        </Tooltip> */}
      </div>
      <section>
        {items.map(album => (
          <div key={album.id} className="mb-40">
            <div className="mb-20 items-center gap-14 md:flex">
              <AlbumImage
                album={album}
                size="w-110 h-110"
                className="flex-shrink-0 rounded object-cover"
              />
              <div className="flex-auto">
                <h4 className="min-w-0 overflow-hidden overflow-ellipsis whitespace-nowrap text-lg font-semibold max-md:mt-12">
                  <AlbumLink album={album} />
                </h4>
                {album.release_date && (
                  <div className="mb-18 mt-2 text-sm text-muted">
                    <FormattedDate date={album.release_date} />
                  </div>
                )}
                <DialogTrigger type="popover" mobileType="tray" offset={10}>
                  <Button
                    variant="outline"
                    size="xs"
                    radius="rounded-full"
                    endIcon={<ArrowDropDownIcon />}
                  >
                    <Trans message="More" />
                  </Button>
                  <AlbumContextDialog album={album} />
                </DialogTrigger>
              </div>
            </div>
            <AlbumTrackTable album={album} />
          </div>
        ))}
        <InfiniteScrollSentinel query={query} />
      </section>
    </>
  );
}

interface AlbumTrackTableProps {
  album: Album;
}
function AlbumTrackTable({album}: AlbumTrackTableProps) {
  const {data, sortDescriptor, onSortChange} = useSortableTableData(
    album.tracks,
  );
  return (
    <TrackTable
      tracks={data}
      hideArtist
      hideAlbum
      hideTrackImage
      sortDescriptor={sortDescriptor}
      onSortChange={onSortChange}
      queueGroupId={queueGroupId(album, '*', sortDescriptor)}
    />
  );
}
